/**
 * Which arrangement of the board fits the screen.
 *
 * A phone gets its own layout (#39): the board as large as the screen allows,
 * the tools in a strip and everything occasional in a sheet. It is chosen by
 * the size of the window, not by sniffing the device, so a narrow desktop
 * window gets it too and a tablet keeps the desktop columns either way round.
 */

import { useSyncExternalStore } from 'react';

export type Layout = 'desktop' | 'portrait' | 'landscape';

/** The tabs of a phone's sheet, where everything used now and then lives. */
export type SheetTab = 'ink' | 'setup' | 'record' | 'more';

/** Narrower than this is a phone held upright… */
export const PHONE_MAX_WIDTH = 599;
/** …and shorter than this is a phone turned sideways. */
export const PHONE_MAX_HEIGHT = 499;

export function layoutFor(width: number, height: number): Layout {
  if (width > PHONE_MAX_WIDTH && height > PHONE_MAX_HEIGHT) return 'desktop';
  return width > height ? 'landscape' : 'portrait';
}

function subscribe(onChange: () => void): () => void {
  window.addEventListener('resize', onChange);
  window.addEventListener('orientationchange', onChange);
  return () => {
    window.removeEventListener('resize', onChange);
    window.removeEventListener('orientationchange', onChange);
  };
}

function current(): Layout {
  return layoutFor(window.innerWidth, window.innerHeight);
}

/** The layout for the window as it is now, following every resize and turn. */
export function useLayout(): Layout {
  return useSyncExternalStore(subscribe, current, () => 'desktop');
}

/** True when the board was opened from the home screen rather than in a browser tab. */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  // iOS reports it on `navigator`; everything else through the display mode.
  const ios = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return ios || window.matchMedia('(display-mode: standalone)').matches;
}
